/**
 * Lightweight per-category draft persistence for the intake modals.
 *
 * Keep it absolutely tiny — we only need to:
 *   - save a draft on field change so a user who closes the modal mid-fill
 *     doesn't lose their inputs;
 *   - hydrate the draft when the modal reopens;
 *   - clear it on successful submit.
 *
 * Drafts are namespaced per category so the Insurance, Auto Loan, and
 * Warranty modals never collide. We deliberately store nothing sensitive
 * (no PII beyond the modal fields themselves), and we treat every
 * localStorage access as best-effort — Safari Private Browsing throws on
 * setItem; we just shrug and move on.
 */
const PREFIX = "carsavr.intake.v1";
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const ENABLED =
  typeof window !== "undefined" &&
  typeof window.localStorage !== "undefined";

function key(category) {
  return `${PREFIX}.${category}`;
}

export function saveIntakeDraft(category, draft) {
  if (!ENABLED || !category || !draft) return;
  try {
    const payload = JSON.stringify({ draft, saved_at: Date.now() });
    window.localStorage.setItem(key(category), payload);
  } catch {
    /* localStorage is full or disabled — best-effort only */
  }
}

export function loadIntakeDraft(category) {
  if (!ENABLED || !category) return null;
  try {
    const raw = window.localStorage.getItem(key(category));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.draft) return null;
    if (Date.now() - (parsed.saved_at || 0) > MAX_AGE_MS) {
      window.localStorage.removeItem(key(category));
      return null;
    }
    return parsed.draft;
  } catch {
    return null;
  }
}

export function clearIntakeDraft(category) {
  if (!ENABLED || !category) return;
  try {
    window.localStorage.removeItem(key(category));
  } catch {
    /* best-effort */
  }
}
